// Option G — caseload triage for doctor / admin.
//
// The assessments list answers "what submissions exist". This answers "who needs
// me this week", which is a different question once a doctor carries more than a
// handful of participants.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle, ArrowDown, ArrowUp, BookOpen, ChevronRight, ChevronsUpDown, Download, Search, Stethoscope, X,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Delta, Score, Meter } from "@/components/clinical/kit";
import { attentionScore, DOMAIN_LABEL, RED_FLAG_MAP, SCORED_WIDS } from "@/config/clinical";
import { interpret } from "@/lib/status";
import { exportCsv } from "@/lib/exportCsv";

const DAY = 86400000;

/** Resolve one participant's ticked indices into urgent / caution counts. */
function tally(flags) {
  let urgent = 0, caution = 0;
  const labels = [];
  for (const [wid, idxs] of Object.entries(flags || {})) {
    const perWid = RED_FLAG_MAP[wid];
    if (!perWid) continue;
    for (const i of idxs || []) {
      const f = perWid[i];
      if (!f) continue;
      if (f.tier === "urgent") urgent += 1; else caution += 1;
      labels.push(f.label);
    }
  }
  return { urgent, caution, labels };
}

// Static class strings — Tailwind cannot see a class built by interpolation.
const ALIGN = { left: "text-left", center: "text-center", right: "text-right" };

function SortHeader({ label, k, sort, onSort, align = "left" }) {
  const { t } = useTranslation();
  const active = sort.key === k;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-stone-500 ${ALIGN[align]}`}>
      <button onClick={() => onSort(k)} data-testid={`panel-sort-${k}`}
        className={`inline-flex items-center gap-1 hover:text-stone-800 ${active ? "text-stone-800" : ""}`}>
        {t(label)} <Icon className="h-3 w-3" />
      </button>
    </th>
  );
}

export default function DoctorPanelPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [sort, setSort] = useState({ key: "attention", dir: "desc" });

  useEffect(() => {
    api.get("/panel")
      .then((r) => setRows(r.data.participants || []))
      .catch((e) => { setErr(formatApiError(e.response?.data?.detail)); setRows([]); });
  }, []);

  const enriched = useMemo(() => (rows || []).map((p) => {
    const f = tally(p.flags);
    const delta = p.overall != null && p.prev_overall != null
      ? Math.round((p.overall - p.prev_overall) * 10) / 10 : null;
    const staleDays = p.latest?.updated_at
      ? Math.floor((Date.now() - new Date(p.latest.updated_at).getTime()) / DAY) : null;
    const weakest = SCORED_WIDS
      .map((wid) => ({ wid, label: DOMAIN_LABEL[wid], score: p.scores?.[wid] }))
      .filter((x) => x.score != null).sort((a, b) => a.score - b.score).slice(0, 3);
    // Ranking, not a clinical score — the weights live in clinical.js so this
    // and the reference screen stay in step.
    const attention = attentionScore({
      urgent: f.urgent, caution: f.caution, delta, overall: p.overall, staleDays,
    });
    return { ...p, ...f, delta, staleDays, weakest, attention };
  }), [rows]);

  const filtered = enriched.filter((p) => {
    if (onlyFlagged && p.urgent + p.caution === 0) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (p.name || "").toLowerCase().includes(q) ||
      (p.email || "").toLowerCase().includes(q) ||
      (p.participant_code || "").toLowerCase().includes(q);
  });

  const val = (p, k) => {
    switch (k) {
      case "name": return (p.name || "").toLowerCase();
      case "stage": return p.latest?.stage || "";
      case "overall": return p.overall ?? -1;
      case "delta": return p.delta ?? 0;
      case "flags": return p.urgent * 100 + p.caution;
      case "complete": return p.scored_complete || 0;
      case "updated": return p.latest?.updated_at ? new Date(p.latest.updated_at).getTime() : 0;
      default: return p.attention;
    }
  };
  const sorted = [...filtered].sort((a, b) => {
    const va = val(a, sort.key), vb = val(b, sort.key);
    if (va < vb) return sort.dir === "asc" ? -1 : 1;
    if (va > vb) return sort.dir === "asc" ? 1 : -1;
    return 0;
  });
  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

  const totals = {
    urgent: enriched.filter((p) => p.urgent > 0).length,
    caution: enriched.filter((p) => p.urgent === 0 && p.caution > 0).length,
    noAssessment: enriched.filter((p) => !p.latest).length,
  };

  const exportPanel = () => {
    exportCsv(`Caseload - ${new Date().toISOString().slice(0, 10)}`, [
      { label: "Participant", key: "name" }, { label: "Code", key: "participant_code" },
      { label: "Email", key: "email" }, { label: "Stage", key: "stage" },
      { label: "Overall", key: "overall" }, { label: "Change", key: "delta" },
      { label: "Urgent flags", key: "urgent" }, { label: "Caution flags", key: "caution" },
      { label: "Flag detail", key: "labels" },
      { label: "Domains scored", key: "scored_complete" }, { label: "Days since update", key: "staleDays" },
      { label: "Weakest domains", key: "weak" },
    ], sorted.map((p) => ({
      ...p, stage: p.latest?.stage || "", overall: p.overall ?? "", delta: p.delta ?? "",
      staleDays: p.staleDays ?? "", labels: p.labels.join("; "),
      weak: p.weakest.map((w) => `${w.label} ${w.score}`).join("; "),
    })));
  };

  if (err) return (
    <AppShell>
      <p className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="panel-error">{err}</p>
    </AppShell>
  );

  return (
    <AppShell>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-serif text-3xl font-bold text-stone-900">
            <Stethoscope className="h-6 w-6 text-emerald-800" />
            {t(user?.role === "admin" ? "All participants" : "My caseload")}
          </h1>
          <p className="text-sm text-stone-500">
            {t("Ranked by clinical attention — urgent findings first, then falling scores, then low averages.")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => nav("/reference")} data-testid="panel-reference"
            className="flex items-center gap-2 rounded-md border border-[#E5DFD3] bg-white px-4 py-2 text-sm font-semibold text-stone-700 hover:bg-stone-50">
            <BookOpen className="h-4 w-4" /> {t("Reference")}
          </button>
          <button onClick={exportPanel} disabled={!sorted.length} data-testid="panel-export"
            className="flex items-center gap-2 rounded-md border border-[#E5DFD3] bg-white px-4 py-2 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40">
            <Download className="h-4 w-4" /> {t("Export")}
          </button>
        </div>
      </div>

      {rows && rows.length > 0 && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { k: "total", label: "Participants", value: enriched.length, tone: "text-stone-800" },
            { k: "urgent", label: "With urgent findings", value: totals.urgent, tone: "text-rose-700" },
            { k: "caution", label: "To discuss", value: totals.caution, tone: "text-amber-700" },
            { k: "none", label: "No assessment yet", value: totals.noAssessment, tone: "text-stone-500" },
          ].map((s) => (
            <div key={s.k} className="rounded-lg border border-[#E5DFD3] bg-white px-4 py-2.5" data-testid={`panel-stat-${s.k}`}>
              <div className="font-mono text-[11px] uppercase tracking-wider text-stone-400">{t(s.label)}</div>
              <div className={`font-serif text-2xl font-bold tabular-nums ${s.tone}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="panel-search"
              placeholder={t("Search by name, code or email…")}
              className="w-full rounded-md border border-[#E5DFD3] bg-white py-2 pl-9 pr-9 text-sm focus:border-emerald-600 focus:outline-none" />
            {query && (
              <button onClick={() => setQuery("")} data-testid="panel-clear-search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-stone-400 hover:text-stone-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <label className="flex items-center gap-1.5 text-sm text-stone-600">
            <input type="checkbox" checked={onlyFlagged} onChange={(e) => setOnlyFlagged(e.target.checked)}
              data-testid="panel-only-flagged" className="h-4 w-4 accent-emerald-700" />
            {t("Only participants with findings")}
          </label>
          <span className="text-xs text-stone-500">{filtered.length} {t("of")} {enriched.length}</span>
        </div>
      )}

      {rows === null ? (
        <p className="text-sm text-stone-400">{t("Loading…")}</p>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#CAD2C5] bg-white p-12 text-center" data-testid="panel-empty">
          <Stethoscope className="mx-auto h-10 w-10 text-emerald-700/40" />
          <p className="mt-3 font-serif text-xl text-stone-700">{t("No participants assigned to you yet")}</p>
          <p className="text-sm text-stone-500">{t("An admin assigns participants to a doctor from the Users page.")}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-[#E5DFD3] bg-white">
          <table className="w-full min-w-[920px] text-sm">
            <thead className="bg-[#E8EFE9] text-left">
              <tr>
                <SortHeader label="Participant" k="name" sort={sort} onSort={toggleSort} />
                <SortHeader label="Stage" k="stage" sort={sort} onSort={toggleSort} />
                <SortHeader label="Findings" k="flags" sort={sort} onSort={toggleSort} />
                <SortHeader label="Overall" k="overall" sort={sort} onSort={toggleSort} align="center" />
                <SortHeader label="Change" k="delta" sort={sort} onSort={toggleSort} align="center" />
                <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-stone-500">{t("Weakest domains")}</th>
                <SortHeader label="Scored" k="complete" sort={sort} onSort={toggleSort} align="center" />
                <SortHeader label="Updated" k="updated" sort={sort} onSort={toggleSort} />
                <th className="w-8 px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => {
                const it = interpret(p.overall);
                const open = () => p.latest && nav(`/assessment/${p.latest.id}/clinical`);
                return (
                  <tr key={p.id} onClick={open} data-testid={`panel-row-${p.id}`}
                    className={`group border-t border-[#EFE9DE] transition ${p.latest ? "cursor-pointer hover:bg-[#F6F8F5]" : "opacity-60"} ${p.urgent ? "bg-rose-50/40" : ""}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-stone-800">{p.name || "—"}</div>
                      <div className="font-mono text-[11px] text-stone-400">
                        {p.participant_code || p.email}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {p.latest ? (
                        <span className="rounded-full bg-[#E8EFE9] px-2 py-0.5 text-xs font-medium text-emerald-800">
                          {t(p.latest.stage)}
                        </span>
                      ) : <span className="text-xs italic text-stone-400">{t("no assessment")}</span>}
                    </td>
                    <td className="px-3 py-2">
                      {p.urgent + p.caution === 0 ? (
                        <span className="text-xs text-stone-400">{t("none")}</span>
                      ) : (
                        <div className="flex flex-wrap items-center gap-1" title={p.labels.join(", ")}>
                          {p.urgent > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-2 py-0.5 text-[11px] font-bold text-white">
                              <AlertTriangle className="h-3 w-3" />{p.urgent}
                            </span>
                          )}
                          {p.caution > 0 && (
                            <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-bold text-white">
                              {p.caution}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Score value={p.overall} color={it.color} size="sm" />
                      <div className="mx-auto mt-0.5 w-14"><Meter value={p.overall} color={it.color} /></div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Delta value={p.delta} />
                      {p.prev_stage && <div className="font-mono text-[10px] text-stone-400">{t("vs")} {t(p.prev_stage)}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {p.weakest.length === 0 ? <span className="text-xs text-stone-400">—</span> : (
                        <div className="flex flex-wrap gap-1">
                          {p.weakest.map((w) => (
                            <span key={w.wid} className="rounded-full border border-[#E5DFD3] px-2 py-0.5 text-[11px] text-stone-600">
                              {t(w.label)} <b style={{ color: interpret(w.score).color }}>{w.score}</b>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center font-mono text-xs tabular-nums text-stone-600">
                      {p.scored_complete}/28
                    </td>
                    <td className="px-3 py-2 text-xs text-stone-500">
                      {p.latest?.updated_at ? new Date(p.latest.updated_at).toLocaleDateString() : "—"}
                      {p.staleDays != null && p.staleDays > 30 && (
                        <div className="text-[11px] text-amber-700">{p.staleDays} {t("days ago")}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {p.latest && <ChevronRight className="h-5 w-5 text-stone-400 group-hover:text-emerald-700" />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
